import Immutable from 'immutable';
import UrlPattern from 'url-pattern';
import moment from 'moment';

import * as Graphdata from 'constants/Graphdata';

import { GRAPHDATA_URL } from 'constants/Jaws';

export function hasSeries(obj) {
  return obj.rows != null;
}

export function getSeries(obj) {
  if (!hasSeries(obj)) return;

  const { series } = obj.rows[0].values;

  return Immutable.fromJS(series)
    .filterNot(({ data }) => data)
    .map((s, key) => {
      return Immutable.fromJS({
        key,
        data: s.get('data'),
        columns: s.get('columns'),
      });
    })
    .toSet();
}

export function getBuckets(obj) {
  if (!obj._embedded || !obj._embedded.graphdata) return;

  const { graphdata } = obj._embedded;

  return Immutable.fromJS(
    graphdata.map(bucket => {
      const { selection, sector } = bucket;
      const { stop, start } = selection;
      const { timezone, close, open } = sector;
      return Immutable.fromJS({
        close,
        open,
        stop,
        start,
        timezone: timezone.shortName,
        UTCOffsetMinutes: timezone.UTCoffsetMinutes,
        link: bucket._links.self.href,
        isFetching: true,
      });
    }),
  );
}

const pattern = new UrlPattern(`/:series/:space/:itemSector`);
export function buildURL(params) {
  return GRAPHDATA_URL + pattern.stringify(params);
}

export function getSpaceFromSelection(selection = Graphdata.SELECTION_WEEK) {
  switch (selection) {
    case Graphdata.SELECTION_INTRADAY:
    case Graphdata.SELECTION_WEEK:
      return 'tick';
    default:
      return 'day';
  }
}

export function getSeriesFromSelection(selection = Graphdata.SELECTION_WEEK) {
  switch (selection) {
    case Graphdata.SELECTION_INTRADAY:
    case Graphdata.SELECTION_WEEK:
      return 's1';
    default:
      return '(CLOSE_CA)';
  }
}

export function getBreaks(state) {
  if (!state.has('buckets') || state.get('buckets').size < 2) return [];

  return state
    .get('buckets')
    .toList()
    .map((v, k, buckets) => {
      const next = buckets.get(k + 1);
      if (!next) return null;

      return {
        from: moment(v.get('close')).valueOf(),
        to: moment(next.get('open')).valueOf(),
      };
    })
    .butLast()
    .toJS();
}

export function buildChartSeries(state) {
  if (!state || !state.has('buckets')) return {};

  const seriesData = state
    .get('buckets')
    .filter(bucket => bucket.has('series'))
    .map(bucket => {
      const open = bucket.get('open');
      const close = bucket.get('close');

      return bucket.get('series').map(series => {
        const data = series.get('data');
        if (!open && !close) {
          return data;
        }

        let lastValue = null;
        if (data.size && !isOpen(bucket)) lastValue = data.last().get(1);

        return Immutable.OrderedSet()
          .add(
            Immutable.fromJS({
              x: moment(open).valueOf(),
              y: null,
              showTooltip: false,
            }),
          )
          .concat(data)
          .add(
            Immutable.fromJS({
              x: moment(close).valueOf(),
              y: lastValue,
              showTooltip: false,
            }),
          );
      });
    })
    .reduce((reduction, val) => reduction.merge(val), Immutable.Set())
    .flatten(true)
    .toOrderedSet()
    .sortBy(entry => {
      if (entry.has('x')) return entry.get('x');
      return entry.get(0);
    })
    .toJS();

  return {
    data: seriesData,
  };
}

function isOpen(bucket) {
  const open = moment.utc(bucket.get('open'));
  const close = moment.utc(bucket.get('close'));
  const now = moment.utc();
  return now.isAfter(open) && now.isBefore(close);
}
